// Karta z API objektu `card` → DOM ve tvaru NATIVNÍ Shoptet karty (issue #67).
//
// Smysl: nestavíme vlastní komponentu. Postavíme přesně ty kotvy, které čte
// `productCard.js` (`enhanceCard`), takže na výsledek sedne celý existující
// skin — **nula nového CSS pro karty**. Kotvy, na kterých to stojí:
//
//   .product > .p[data-micro=product]     obal + karta (grid bere `.product`)
//   a.image > img                         fotka
//   .flags .flag                          vlaječky → badgy
//   .price-save                           procenta slevy
//   .stars-wrapper[data-micro-rating-*]   hodnocení
//   .availability + .availability-amount  dostupnost
//   .prices > .price.price-final > strong cena (stěhuje se do fotky)
//   .add-to-cart-button                   CTA
//
// ⚠️ ODCHYLKA od nativní karty: CTA NENÍ `form.pr-action`. Nativní formulář
// potřebuje hidden `priceId` a `productId`, a index ani jedno nemá — `price_id`
// je vždy `null` (Shoptet Private API ho nevydává, viz search/README.md) a
// `card.id` nese KÓD produktu, ne číselné id. Probe (#67) potvrdil, že
// `shoptet.cartShared.addToCart({productCode, amount})` funguje i pro DOM
// vzniklý po `DOMContentLoaded` → CTA je tlačítko s naším handlerem.
// Košík tím pořád obsluhuje výhradně Shoptet, jen se volá jinou cestou.
//
// ⚠️ ZNÁMÁ OMEZENÍ DAT (změřeno proti živému API 2026-08-03):
//   • `flags[]` je prázdné u VŠECH produktů (indexer `badges` neplní) → naše
//     karty nemají Novinku/Tip/Akci, nativní ano. Kosmetický rozdíl, follow-up.
//   • `availability.amount` je `0` u ~60 % skladem dostupných položek — nula
//     tu znamená „neznámo", ne „došlo". Proto se `(N ks)` kreslí jen při
//     `amount > 0`; jinak by karta hlásila „(0 ks)" a `productCard.js` by ji
//     obarvil oranžově jako nízký sklad.

import { addToCart } from '../shoptet-hooks.js';
import { discountPercent } from './productPrice.js';
import { stockAmountText } from './stockAmount.js';
import { withCurrency, number } from './money.js';
import L from '../locale/cs/productCardRender.js';

const LABEL_CART = L.cart;
const LABEL_DETAIL = L.detail;
const IN_STOCK_COLOR = '#009901';

function el(tag, cls, txt) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (txt != null) node.textContent = txt;
  return node;
}

/** „1 234 Kč" — API posílá `price_formatted` hotové, tohle je jen pojistka. */
function priceText(card) {
  if (card.price_formatted) return card.price_formatted;
  return card.price === null || card.price === undefined ? '' : withCurrency(number(card.price));
}

// ---------------------------------------------------------------- části karty

function buildImage(card) {
  const link = el('a', 'image');
  link.href = card.url || '';
  if (card.image) {
    const img = el('img');
    img.src = card.image;
    img.alt = card.name || '';
    img.setAttribute('data-micro-image', card.image);
    img.width = 423;
    img.height = 318;
    img.loading = 'lazy'; // mřížka výsledků je pod ohybem, na rozdíl od hero
    link.appendChild(img);
  }
  // `flags` z indexu chodí prázdné (viz hlavička) — blok se staví jen když
  // opravdu něco přijde, ať v DOM nezůstane prázdný `.flags`.
  // Index posílá akční štítky holými slovy `akce` / `sleva` (search/api/card.mjs
  // z `badges`): `akce` = nativní vlaječka akční nabídky (productCard.js z ní udělá
  // jeden akční štítek, po dobu kampaně s názvem deštníku), `sleva` odpadá, slevu
  // kreslí karta z přeškrtnuté ceny („−N %"). Dřív karta hledání ukazovala „AKCE".
  const flags = (card.flags || []).filter(Boolean).reduce(function (out, f) {
    if (f === 'akce') out.push({ type: 'action', title: L.flagAction });
    else if (f !== 'sleva') out.push(f);
    return out;
  }, []);
  if (flags.length) {
    const box = el('div', 'flags flags-default');
    flags.forEach(function (flag) {
      const label = typeof flag === 'string' ? flag : flag.title || flag.name || '';
      if (!label) return;
      const cls = typeof flag === 'string' ? '' : flag.type || '';
      box.appendChild(el('span', 'flag' + (cls ? ' flag-' + cls : ''), label));
    });
    if (box.children.length) link.appendChild(box);
  }
  return link;
}

function buildName(card) {
  const link = el('a', 'name');
  link.href = card.url || '';
  link.setAttribute('data-micro', 'url');
  const span = el('span', null, card.name || '');
  span.setAttribute('data-micro', 'name');
  span.setAttribute('data-testid', 'productCardName');
  link.appendChild(span);
  return link;
}

// `.stars-wrapper` čte productCard.js přes data-atributy; hvězdičky samotné
// nekreslíme, ten si řádek `★ 4,5 · 2 recenze` staví sám.
function buildRating(card) {
  const rating = card.rating || {};
  if (!rating.value) return null;
  const wrap = el('span', 'stars-wrapper');
  wrap.setAttribute('data-micro-rating-value', String(rating.value));
  wrap.setAttribute('data-micro-rating-count', String(rating.count || 0));
  return wrap;
}

function buildAvailability(card) {
  const avail = card.availability || {};
  if (!avail.label) return null;

  const box = el('div', 'availability');
  const label = el('span', null, avail.label);
  if (avail.state === 'in_stock') label.style.color = IN_STOCK_COLOR;
  box.appendChild(label);

  // POZOR: `amount === 0` znamená „neznámo", ne „došlo" (viz hlavička souboru).
  // Nad 5 kus\u016F \u201E(>5 ks)" jako nativn\u00ED karta Shoptetu, ne p\u0159esn\u00FD po\u010Det z indexu.
  const amountText = stockAmountText(avail.amount);
  if (amountText) {
    const amount = el('span', 'availability-amount', '(' + amountText + ')');
    amount.setAttribute('data-testid', 'numberAvailabilityAmount');
    box.appendChild(amount);
  }
  return box;
}

function buildPrices(card) {
  const box = el('div', 'prices');

  // Přeškrtnutá cena + procenta slevy. `price_before` dnes z indexu nechodí
  // (0 z 40 vzorku), ale kontrakt ho má — až doteče, karta ho unese.
  if (card.price_before && card.price) {
    box.appendChild(el('div', 'price price-standard', withCurrency(number(card.price_before))));
    const pct = discountPercent(card.price_before, card.price); // dolů jako Shoptet
    if (pct) box.appendChild(el('span', 'price-save', '(–' + pct + ' %)'));
  }

  const final = el('div', 'price price-final');
  final.setAttribute('data-testid', 'productCardPrice');
  final.appendChild(el('strong', null, priceText(card)));
  box.appendChild(final);
  return box;
}

/**
 * CTA. Varianty a nedostupné zboží vedou na detail (do košíku je z výpisu
 * pustit nelze — je potřeba vybrat variantu), zbytek přidává rovnou.
 */
function buildCta(card) {
  const tools = el('div', 'p-tools');

  if (card.variants || !card.sku || !card.in_stock) {
    const link = el('a', 'btn btn-cart add-to-cart-button');
    link.href = card.url || '';
    link.appendChild(el('span', null, LABEL_DETAIL));
    tools.appendChild(link);
    return tools;
  }

  const btn = el('button', 'btn btn-cart add-to-cart-button');
  btn.type = 'button';
  btn.setAttribute('data-testid', 'buttonAddToCart');
  btn.setAttribute('aria-label', L.cartAria(card.name));
  btn.appendChild(el('span', null, LABEL_CART));
  btn.addEventListener('click', function () {
    // Nedaří-li se sáhnout na Shoptet API (starý build, blokovaný JS), je
    // odchod na detail lepší než tiše spolknutý klik.
    if (!addToCart({ productCode: card.sku, amount: 1 })) window.location.href = card.url || '';
  });
  tools.appendChild(btn);
  return tools;
}

// ---------------------------------------------------------------- složení

/**
 * `card` (API) → `div.product` připravený k vložení do mřížky.
 * Po vložení je potřeba zavolat `productCard.enhanceCards(grid)`, který
 * z těchto kotev udělá náš skin.
 */
export function cardNode(card) {
  const wrap = el('div', 'product');

  const p = el('div', 'p');
  p.setAttribute('data-micro', 'product');
  // konec akční ceny z indexu → segment pilulky kreslí saleEndCards.js bez dotazu
  if (typeof card.action_to === 'string' && card.action_to) p.setAttribute('data-bs-sale-to', card.action_to);
  // bestseller z indexu → samolepku kreslí productCard.js při dekoraci (bez dotazu)
  if (card.bestseller === true) p.setAttribute('data-bs-bestseller', '1');
  p.setAttribute('data-testid', 'productItem');
  if (card.id) p.setAttribute('data-micro-product-id', String(card.id));
  if (card.guid) p.setAttribute('data-micro-identifier', card.guid);
  // Kotva pro analytiku (#68). Nativní `data-micro-*` nesou id a guid, ale
  // v indexu i v BQ se produkt páruje přes KÓD — proto vlastní atribut.
  if (card.sku) p.setAttribute('data-bs-code', card.sku);
  // variantní karta: cena se mění s variantou → VO štítek (wholesalePrice.js) ji přeskakuje
  if (card.variants) p.setAttribute('data-bs-variants', '1');

  p.appendChild(buildImage(card));

  const inner = el('div', 'p-in');
  const innerIn = el('div', 'p-in-in');
  innerIn.appendChild(buildName(card));

  const ratings = el('div', 'ratings-wrapper');
  const rating = buildRating(card);
  if (rating) ratings.appendChild(rating);
  const avail = buildAvailability(card);
  if (avail) ratings.appendChild(avail);
  innerIn.appendChild(ratings);
  inner.appendChild(innerIn);

  const bottom = el('div', 'p-bottom');
  const offer = el('div');
  offer.setAttribute('data-micro', 'offer');
  if (card.price !== null && card.price !== undefined) {
    offer.setAttribute('data-micro-price', String(card.price));
    offer.setAttribute('data-micro-price-currency', 'CZK');
  }
  offer.setAttribute(
    'data-micro-availability',
    card.in_stock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
  );
  offer.appendChild(buildPrices(card));
  offer.appendChild(buildCta(card));
  bottom.appendChild(offer);
  inner.appendChild(bottom);

  p.appendChild(inner);
  wrap.appendChild(p);
  return wrap;
}

/** Pole karet → `DocumentFragment` (jeden reflow místo N). */
export function cardNodes(cards) {
  const frag = document.createDocumentFragment();
  (cards || []).forEach(function (card) {
    frag.appendChild(cardNode(card));
  });
  return frag;
}
